import { useMemo, useState } from 'react'
import {
  faArrowUpRightFromSquare,
  faTrash,
  faXmark,
  faArrowsRotate,
} from '@fortawesome/pro-regular-svg-icons'
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import { FormikProvider, useFormik } from 'formik'

import {
  Badge,
  Button,
  Card,
  CardColors,
  Dropdown,
  Form,
  Icon,
  Link,
  Popup,
  Text,
} from '@/ui'
import {
  addCurrencyArray,
  dollarsToCurrency,
  formatCurrency,
} from '@/utils/currencyHelpers'
import { IncomeCalculatorDialog } from '@/features/goal-setting/IncomeCalculatorDialog'
import { getFieldNames, makeNumberSchema, makeReqStringSchema } from '@/base'
import { TypographyStyles } from '@/styles'

export enum IncomeType {
  hourlyRate = 'hourlyRate',
  periodic = 'periodic',
}

export interface HourlyRateIncome {
  incomeType: IncomeType.hourlyRate
  name: string
  rate: number
  amountPerPeriod: number
  period: PeriodicTimePeriod
}

export type PeriodicTimePeriod = 'perWeek' | 'perMonth' | 'perYear'

export interface PeriodicIncome {
  incomeType: IncomeType.periodic
  name: string
  rate: number
  amountPerPeriod: null
  period: PeriodicTimePeriod
}

const DEFAULT_WEEKS_OFF = 4

export const IncomeCalculator = ({
  currentGoal,
  updateIncomeGoal,
}: {
  currentGoal: number | null
  updateIncomeGoal: (goal: number) => void
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIncomeType, setModalIncomeType] = useState(IncomeType.hourlyRate)

  const formik = useFormik({
    initialValues: {
      weeksOff: DEFAULT_WEEKS_OFF,
      income: [] as Array<HourlyRateIncome | PeriodicIncome>,
    },
    onSubmit: () => {
      // Initial implementation does not have a submit
    },
  })
  const fieldNames = getFieldNames(formik)

  const { values, setFieldValue, isValid, setValues } = formik

  const totalProjection = useMemo(() => {
    if (!isValid) {
      return null
    }

    const percentageOfYearToWork = (52 - values.weeksOff) / 52

    return addCurrencyArray(
      values.income.map((income) => {
        const base =
          income.incomeType === IncomeType.hourlyRate
            ? dollarsToCurrency(income.rate)
                .multiply(income.amountPerPeriod)
                .multiply(percentageOfYearToWork)
            : dollarsToCurrency(income.rate)

        if (income.period === 'perWeek') {
          return base.multiply(52)
        } else if (income.period === 'perMonth') {
          return base.multiply(12)
        }

        return base
      })
    )
  }, [isValid, values.income, values.weeksOff])

  const goalDiff =
    currentGoal && totalProjection
      ? ((totalProjection - currentGoal) / currentGoal) * 100
      : null

  return (
    <FormikProvider value={formik}>
      <Card className="w-full">
        <div className="flex justify-between">
          <Text variant={TypographyStyles.H1}>Income Calculator</Text>

          <Link
            href="https://support.joinheard.com/hc/en-us/articles/32478412685463/"
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            newPage
          >
            Calculator Guide
            <Icon icon={faArrowUpRightFromSquare} style={{ marginLeft: 10 }} />
          </Link>
        </div>
        <Form.Input
          label="Weeks off per year"
          name={fieldNames.weeksOff}
          schema={makeNumberSchema({ minNumber: 0 })}
          wrapperClassName="mt-6"
          afterLabel={
            <Popup
              content={
                <>
                  <Text variant={TypographyStyles.H3}>
                    Calculating Weeks Off Per Year
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                    Enter your total planned weeks off for the year. Include
                    partial weeks: 5 individual days off (like 5 Fridays) count
                    as 1 full week. So, if you also took a 3-week vacation, your
                    total would be 4 weeks off. We&apos;ll use this to adjust
                    your projected income for your client session and hourly
                    income streams (not your periodic income streams)
                  </Text>
                </>
              }
            />
          }
        />
        <Form.Slider
          name={fieldNames.weeksOff}
          schema={makeNumberSchema({ minNumber: 0 })}
          min={0}
          step={1}
          max={15}
          wrapperClassName="mt-4"
        />

        {values.income.map((income, index) => (
          <Card key={index} className="mt-6">
            <div className="flex justify-between">
              <Text variant={TypographyStyles.LABEL_MD}>{income.name}</Text>

              <Button
                variant="secondary"
                onClick={() => {
                  const newList = [...values.income]
                  newList.splice(index, 1)
                  setFieldValue(fieldNames.income, newList)
                }}
              >
                <Icon icon={faTrash} />
              </Button>
            </div>
            {income.incomeType === IncomeType.hourlyRate && (
              <div className="flex gap-3 mt-4">
                <div className="w-1/2">
                  <Form.Input
                    name={`income[${index}].rate`}
                    label="Session/hourly rate"
                    componentType="currency"
                    schema={makeNumberSchema({
                      allowedDecimals: 2,
                      minNumber: 0,
                    })}
                  />
                  <Form.Slider
                    name={`income[${index}].rate`}
                    min={0}
                    step={5}
                    max={400}
                    wrapperClassName="mt-4"
                  />
                </div>
                <div className="w-1/2">
                  <Form.Input
                    name={`income[${index}].amountPerPeriod`}
                    label={
                      income.period === 'perYear'
                        ? '# per year'
                        : income.period === 'perMonth'
                          ? '# per month'
                          : '# per week'
                    }
                    schema={makeNumberSchema({
                      minNumber: 0,
                    })}
                  />
                  <Form.Slider
                    name={`income[${index}].amountPerPeriod`}
                    min={0}
                    step={1}
                    max={240}
                    wrapperClassName="mt-4"
                  />
                </div>
              </div>
            )}
            {income.incomeType === IncomeType.periodic && (
              <div className="flex gap-3 mt-4">
                <div className="w-1/2">
                  <Form.Input
                    name={`income[${index}].rate`}
                    label="Amount"
                    componentType="currency"
                    schema={makeNumberSchema({
                      allowedDecimals: 2,
                      minNumber: 0,
                    })}
                  />
                  <Form.Slider
                    name={`income[${index}].rate`}
                    min={0}
                    step={5}
                    max={400}
                    wrapperClassName="mt-4"
                  />
                </div>
                <Form.Select
                  name={`income[${index}].period`}
                  options={[
                    { value: 'perWeek', text: 'Per week' },
                    { value: 'perMonth', text: 'Per month' },
                    { value: 'perYear', text: 'Per year' },
                  ]}
                  label="Period"
                  schema={makeReqStringSchema()}
                  wrapperClassName="w-1/2"
                />
              </div>
            )}
          </Card>
        ))}

        <div className="w-full mt-6">
          <Dropdown>
            <Dropdown.Trigger className="w-full">
              <Button
                className="w-full"
                variant="secondary"
                iconRight={faCaretDown}
              >
                Add Income
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Content className="w-full">
              <Dropdown.Item
                onClick={() => {
                  setModalIncomeType(IncomeType.hourlyRate)
                  setModalOpen(true)
                }}
                className="w-full text-lg"
              >
                Hourly rate income
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setModalIncomeType(IncomeType.periodic)
                  setModalOpen(true)
                }}
                className="w-full text-lg"
              >
                Periodic income
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        </div>

        <Card color={CardColors.PRIORITY} className="mt-6">
          <Text variant={TypographyStyles.LABEL_SM}>
            Projected Annual Income
            <Popup
              content={
                <>
                  <Text variant={TypographyStyles.H3}>
                    Projected Annual Income
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                    We project your annual income from sessions/hourly rates
                    based on your actual working weeks (52 minus weeks off).
                    Other periodic income is added as its full yearly amount.
                    For example, if you work 48 weeks, $1,000/week from one
                    session source is $48,000, and $500/week from another is
                    $24,000, totaling $72,000 from sessions. If you also have
                    $10,000 in other fixed periodic income, your total
                    projection would be $82,000.
                  </Text>
                </>
              }
            />
          </Text>
          {totalProjection ? (
            <div className="mt-2 flex items-center">
              <Text variant={TypographyStyles.DISPLAY_XS}>
                {formatCurrency(totalProjection, {
                  hideDecimalsIfZero: true,
                })}
              </Text>
              {Boolean(goalDiff && currentGoal) && goalDiff && (
                <Badge
                  color={goalDiff > 0 ? 'green' : 'red'}
                  className="ml-2"
                  variant="secondary"
                >
                  {goalDiff > 0 ? '+' : ''}
                  {goalDiff.toFixed(2)}% vs Goal
                </Badge>
              )}
            </div>
          ) : (
            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              $-
            </Text>
          )}
          <div className="flex gap-3 mt-6">
            <Button
              variant="secondary"
              onClick={() =>
                setValues({ weeksOff: DEFAULT_WEEKS_OFF, income: [] })
              }
              className="w-1/2"
            >
              <Icon icon={faXmark} style={{ marginRight: 8 }} />
              Clear Calculator
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                totalProjection && updateIncomeGoal(totalProjection)
              }
              disabled={!totalProjection}
              className="w-1/2"
            >
              <Icon icon={faArrowsRotate} style={{ marginRight: 8 }} />
              Update Income Goal
            </Button>
          </div>
        </Card>
      </Card>
      {modalOpen && (
        <IncomeCalculatorDialog
          onCancel={() => setModalOpen(false)}
          onSubmit={(income: HourlyRateIncome | PeriodicIncome) => {
            setFieldValue(fieldNames.income, [...values.income, income])
            setModalOpen(false)
          }}
          incomeType={modalIncomeType}
        />
      )}
    </FormikProvider>
  )
}
